import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { ErrorBoundary } from "./shared/components/feedback/ErrorBoundary";
import { useToken } from "./context/TokenContext";
import { registerTokenGetter } from "./api/config/axios";

// ✅ EAGER LOADING - Critical pages loaded immediately (Login, Home, Store Select)
import { LoginPage } from "./pages/Login";
import { HomePage } from "./pages/homepage/Home";
import { StoreSelect } from "./pages/StoreSelect";
import { LogoutPage } from "./pages/logoutPage";
import { UndefinePage } from "./pages/Undefine";
import DummyDataGenerator from "./pages/internalDev/DummyDataGenerator";
import { ClientBooking } from "./pages/internalDev/ClientBooking";

// ✅ LAZY LOADING - Admin pages loaded on-demand (reduces initial bundle by ~70%)
// These pages are only loaded when user navigates to them
const SignupPage = lazy(() => import("./pages/signup").then(m => ({ default: m.SignupPage })));
const ResetPasswordPage = lazy(() => import("./pages/forgetPassPage"));
const TermsPage = lazy(() => import("./pages/termsPage").then(m => ({ default: m.TermsPage })));
const InstallationDocumentation = lazy(() => import("./pages/homepage/installDoc"));

// Admin pages (lazy loaded)
const MiniDrawer = lazy(() => import("./components/DrawerLayout").then(m => ({ default: m.MiniDrawer })));
const Adash = lazy(() => import("./pages/admin/Adash").then(m => ({ default: m.Adash })));
const MenuPage = lazy(() => import("./pages/admin/MenuPage").then(m => ({ default: m.MenuPage })));
const Order = lazy(() => import("./pages/admin/order").then(m => ({ default: m.Order })));
const InventoryPage = lazy(() => import("./pages/admin/Inventory/Inventory").then(m => ({ default: m.InventoryPage })));
const LayoutBuilder = lazy(() => import("./LayoutBuilder/layoutBuilder"));
const Online = lazy(() => import("./pages/Online").then(m => ({ default: m.Online })));
const SettingsPage = lazy(() => import("./pages/Settings").then(m => ({ default: m.SettingsPage })));
const UserInfoScreen = lazy(() => import("./pages/admin/UserInfoScreen"));
const AdvanceSettingDevices = lazy(() => import("./pages/settings/AdvanceSettingDevices").then(m => ({ default: m.AdvanceSettingDevices })));
const ReportPage = lazy(() => import("./pages/admin/report/Report"));
const PrintLayoutSettings = lazy(() => import("./pages/settings/printerLayouts").then(m => ({ default: m.PrintLayoutSettings })));
const TimePunchManager = lazy(() => import("./pages/admin/timePunch"));
const SideCarPage = lazy(() => import("./pages/admin/sideCarMagmt"));
const PromotionsPage = lazy(() => import("./pages/promotions/index"));
const CustomerLoyalty = lazy(() => import("./pages/admin/CustomerLoyalty"));
const AppointmentsPage = lazy(() => import("./pages/admin/appointments/AppointmentsPage"));
const BookingHub = lazy(() => import("./pages/admin/bookings/BookingHub"));

// Report pages (lazy loaded)
const ReportWrapper = lazy(() => import("./pages/admin/report/ReportWrapper").then(m => ({ default: m.ReportWrapper })));
const InventoryReportPage = lazy(() => import("./pages/admin/report/components/InventoryReportPage").then(m => ({ default: m.InventoryReportPage })));
const InventoryLogReportPage = lazy(() => import("./pages/admin/report/components/InventoryLogReportPage").then(m => ({ default: m.InventoryLogReportPage })));

// Other pages (lazy loaded)
const FranchiseDashboard = lazy(() => import("./pages/franchise/franchise"));
const WebSocketOrderTester = lazy(() => import("./pages/internalDev/WebSocketOrderTester"));
// const MiiraMasterController = lazy(() => import("./temp/ws-push"));

// ✅ Loading Fallback Component - Shown while lazy components load
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}
  >
    <CircularProgress size={60} thickness={4} />
  </Box>
);


// Component to register token getter with axios
function TokenBridge() {
  const { getAccessToken, setAccessToken, clearAccessToken } = useToken();

  // Register synchronously so child components can read immediately
  registerTokenGetter(getAccessToken);
  if (typeof window !== 'undefined') {
    window.__getAccessToken = getAccessToken;
    window.__setAccessToken = setAccessToken;
    window.__clearAccessToken = clearAccessToken;
  }

  useEffect(() => {
    registerTokenGetter(getAccessToken);
    window.__setAccessToken = setAccessToken;
    window.__clearAccessToken = clearAccessToken;
    window.__getAccessToken = getAccessToken;
    
    return () => {
      window.__setAccessToken = undefined;
      window.__clearAccessToken = undefined;
      window.__getAccessToken = undefined;
    };
  }, [getAccessToken, setAccessToken, clearAccessToken]);

  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <TokenBridge />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route>
            <Route path="/" element={<HomePage />} />
            <Route path="/support" element={<InstallationDocumentation />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<SignupPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

          </Route>
          <Route path="/store" element={<StoreSelect />} />
          <Route path="/logout" element={<LogoutPage />} />

          {/*  */}
          <Route path="/tip" element={<ReportWrapper />} />
          <Route path="/clock-in" element={<ReportWrapper />} />
          <Route path="/epr" element={<ReportWrapper />} />
          <Route path="/sales-report" element={<ReportWrapper />} />
          <Route path="/orders-report" element={<ReportWrapper />} />
          <Route path="/item-report" element={<ReportWrapper />} />
          <Route path="/inventory-report" element={<InventoryReportPage />} />
          <Route path="/inventory-report-logs" element={<InventoryLogReportPage />} />
          <Route path="/admin" element={<MiniDrawer />}>
            <Route path="" element={<Adash />} />
            <Route path="order" element={<Order />} />
            <Route path="booking" element={<BookingHub />} />
            {/* Legacy routes - keeping for backward compatibility */}
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="menu" element={<MenuPage />} />
            <Route path="side-car" element={<SideCarPage />} />
            <Route path="promotions" element={<PromotionsPage />} />
            <Route path="customers" element={<CustomerLoyalty />} />
            <Route path="layout" element={<LayoutBuilder />} />
            <Route path="online" element={<Online />} />
            <Route path="reports" element={<ReportPage />} />
            <Route path="time-punch" element={<TimePunchManager />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="user" element={<UserInfoScreen />} />
            <Route path="deviceAdvSet" element={<AdvanceSettingDevices />} />
            <Route path="printerLayouts" element={<PrintLayoutSettings />} />
            {/* <Route path="profile" element={<ProfilePage />} /> */}
            {/* following is temporary.. */}
            {/* <Route path="create" element={<CreateOrder />} /> */}
          </Route>
          <Route path="/franchise/:franchiseId" element={<FranchiseDashboard />} />
          
          
          <Route path="/terms" element={<TermsPage />} />      
          <Route path="*" element={<UndefinePage />} />
          
          {/* Internal development tools */}
          <Route path="/dummy" element={<DummyDataGenerator />} />
          <Route path="/ws-tester" element={<WebSocketOrderTester />} />
          
          {/* Public booking page */}
          <Route path="/salcli" element={<ClientBooking />} />

          </Routes>
        </Suspense>
    </ErrorBoundary>
  )
}
